'use client';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { hospitalI18n } from '../data/hospitals_i18n';
import { hospitals, Hospital } from '../data/hospitals';
import { sidoList, gugunData } from '../data/regions';
import { sidoI18n, gugunI18n } from '../data/regions_i18n';

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';

declare global {
    interface Window {
        google: any;
        gtag?: (...args: any[]) => void;
    }
}

export default function Section9_FindHospital() {
    const { t, language } = useLanguage();

    // 한국어 화면은 원문 그대로. 다른 언어는 '현지표기 (한국어 원문)' 로 함께 보여준다.
    // 한국어를 남기는 이유: 택시·내비게이션에 그대로 보여줄 수 있어야 하기 때문이다.
    // 드롭다운 라벨. value 는 한국어 원문을 유지해야 필터가 동작한다 (h.sido === selectedSido).
    // 보이는 글자만 '현지표기 (한국어)' 로 바꾼다.
    const regionLabel = (ko: string, table: Record<string, { en: string; zh: string; mn: string }>) => {
        if (language === 'ko') return ko;
        const e = table[ko];
        const v = e && (e as any)[language];
        return v ? `${v} (${ko})` : ko;
    };

    const localized = (id: number, field: 'name' | 'address', ko: string) => {
        if (language === 'ko') return { main: ko, sub: '' };
        const e = hospitalI18n[id];
        const v = e && (e as any)[language] ? (e as any)[language][field] : '';
        return v ? { main: v, sub: ko } : { main: ko, sub: '' };
    };

    const sectionRef = useRef<HTMLDivElement>(null);
    const observer = useIntersectionObserver(sectionRef, { threshold: 0.01, freezeOnceVisible: true });
    const isVisible = !!observer?.isIntersecting;

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSido, setSelectedSido] = useState('');
    const [selectedGugun, setSelectedGugun] = useState('');
    const [selectedHospitalId, setSelectedHospitalId] = useState<number | null>(null);

    const mapRef = useRef<HTMLDivElement>(null);
    const googleMap = useRef<any>(null);
    const markers = useRef<any[]>([]);
    const infoWindow = useRef<any>(null);

    // Extract gugun list based on selected sido
    const gugunList = useMemo(() => {
        if (!selectedSido) return [];
        return gugunData[selectedSido] || [];
    }, [selectedSido]);

    // Handle search and filtering
    const filteredHospitals = useMemo(() => {
        const filtered = hospitals.filter(h => {
            // 한국어 원문뿐 아니라 현지 표기로도 검색되게 한다
            const q = searchTerm.toLowerCase();
            const i18 = hospitalI18n[h.id];
            const alt = i18 ? Object.values(i18).flatMap(v => [v.name, v.address]) : [];
            // 지역명도 각 언어 표기로 검색되게 한다 (예: 江南, Gangnam, Ганнам)
            const rg = gugunI18n[h.gugun];
            const rs = sidoI18n[h.sido];
            if (rg) alt.push(rg.en, rg.zh, rg.mn);
            if (rs) alt.push(rs.en, rs.zh, rs.mn);
            const matchesSearch = searchTerm === '' ||
                h.name.toLowerCase().includes(q) ||
                h.address.toLowerCase().includes(q) ||
                alt.some(v => v.toLowerCase().includes(q));
            const matchesSido = selectedSido === '' || h.sido === selectedSido;
            const matchesGugun = selectedGugun === '' || h.gugun === selectedGugun;
            return matchesSearch && matchesSido && matchesGugun;
        });

        // 대구가 선택된 경우에만 (대구)메트로안과를 최상단으로 올림
        return filtered.sort((a, b) => {
            if (selectedSido === '대구') {
                if (a.name === '(대구)메트로안과') return -1;
                if (b.name === '(대구)메트로안과') return 1;
            }

            // 특수문자(괄호)로 인한 정렬 왜곡을 막기 위해 괄호를 제거한 텍스트로 비교 (전체 보기용)
            const nameA = a.name.replace(/^\(.*?\)\s*/, '');
            const nameB = b.name.replace(/^\(.*?\)\s*/, '');
            
            return nameA.localeCompare(nameB, 'ko');
        });
    }, [searchTerm, selectedSido, selectedGugun]);

    // Google Maps Integration
    useEffect(() => {
        if (typeof window === 'undefined' || !observer?.isIntersecting || googleMap.current) return;

        const loadGoogleMaps = () => {
            if (window.google && window.google.maps) {
                initMap();
                return;
            }

            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = initMap;
            document.head.appendChild(script);
        };

        const initMap = () => {
            if (!mapRef.current) return;
            googleMap.current = new window.google.maps.Map(mapRef.current, {
                center: { lat: 37.5665, lng: 126.9780 }, // Center on Seoul
                zoom: 11,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
            });
            infoWindow.current = new window.google.maps.InfoWindow({
                pixelOffset: new window.google.maps.Size(0, -40)
            });
            updateMarkers();
        };

        loadGoogleMaps();
    }, [isVisible]);

    const updateMarkers = () => {
        if (!googleMap.current) return;

        // Clear existing markers
        markers.current.forEach(m => m.setMap(null));
        markers.current = [];

        const bounds = new window.google.maps.LatLngBounds();
        let validMarkers = 0;

        filteredHospitals.forEach((h) => {
            if (h.lat && h.lng) {
                const marker = new window.google.maps.Marker({
                    position: { lat: h.lat, lng: h.lng },
                    map: googleMap.current,
                    title: h.name
                });

                marker.addListener('click', () => {
                    setSelectedHospitalId(prev => prev === h.id ? null : h.id);
                });

                markers.current.push(marker);
                bounds.extend({ lat: h.lat, lng: h.lng });
                validMarkers++;
            }
        });

        if (validMarkers > 0) {
            googleMap.current.fitBounds(bounds);
            if (validMarkers === 1) {
                googleMap.current.setZoom(15);
            }
        }
    };

    useEffect(() => {
        updateMarkers();
    }, [filteredHospitals]);

    useEffect(() => {
        if (selectedHospitalId && googleMap.current) {
            const hospital = filteredHospitals.find(h => h.id === selectedHospitalId);
            if (hospital && hospital.lat && hospital.lng) {
                googleMap.current.panTo({ lat: hospital.lat, lng: hospital.lng });
                googleMap.current.setZoom(16);
                
                if (infoWindow.current) {
                    const safeName = hospital.name.replace(/'/g, "\\'");
                    const visitSiteLabel = t(translations.findHospital.visitSite);
                    const contentString = `
                        <div style="padding: 10px; max-width: 250px; box-sizing: border-box; color: #333; font-family: 'Paperlogy', sans-serif;">
                            <h3 style="margin: 0 0 8px 0; font-size: 15px; font-weight: bold; color: #0055A5; word-wrap: break-word;">${localized(hospital.id, 'name', hospital.name).main}${localized(hospital.id, 'name', hospital.name).sub ? `<br><span style="font-weight:400;font-size:12px;color:#8b93a6">${localized(hospital.id, 'name', hospital.name).sub}</span>` : ''}</h3>
                            <p style="margin: 0 0 5px 0; font-size: 13px; line-height: 1.4; word-wrap: break-word;">${localized(hospital.id, 'address', hospital.address).main}${localized(hospital.id, 'address', hospital.address).sub ? `<br><span style="font-size:12px;color:#8b93a6">${localized(hospital.id, 'address', hospital.address).sub}</span>` : ''}</p>
                            ${hospital.phone ? `<p style="margin: 0 0 5px 0; font-size: 13px; word-wrap: break-word;">📞 <a href="tel:${hospital.phone}" style="color: #0055A5; text-decoration: none;" onclick="window.gtag && window.gtag('event', 'click_call', { event_category: 'hospital_finder', hospital_name: '${safeName}' })">${hospital.phone}</a></p>` : ''}
                            ${hospital.website ? `<p style="margin: 0; font-size: 13px; word-wrap: break-word;">🌐 <a href="${hospital.website}" target="_blank" rel="noopener noreferrer" style="color: #0055A5; text-decoration: none;" onclick="window.gtag && window.gtag('event', 'click_website', { event_category: 'hospital_finder', hospital_name: '${safeName}' })">${visitSiteLabel}</a></p>` : ''}
                        </div>
                    `;
                    infoWindow.current.setContent(contentString);
                    infoWindow.current.setPosition({ lat: hospital.lat, lng: hospital.lng });
                    infoWindow.current.open(googleMap.current);
                }
            }
        } else if (infoWindow.current) {
            infoWindow.current.close();
        }
    }, [selectedHospitalId, filteredHospitals]);

    const handleHospitalClick = (id: number) => {
        setSelectedHospitalId(prev => prev === id ? null : id);
    };

    const logHospitalAction = (actionType: 'click_website' | 'click_call', hospitalName: string) => {
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', actionType, {
                event_category: 'hospital_finder',
                hospital_name: hospitalName,
            });
        }
    };

    return (
        <section id="find-hospital" ref={sectionRef} className="section-find-hospital">
            <div className="container">
                <header className={`header-content reveal ${isVisible ? 'active' : ''}`}>
                    <h2 className="title">{t(translations.findHospital.title)}</h2>
                    <p className="subtitle">{t(translations.cta.title)}</p>

                    <div className="search-container glass">
                        <input
                            type="text"
                            placeholder={t(translations.findHospital.searchPlaceholder)}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="filter-group">
                            <select 
                                aria-label={t(translations.findHospital.regionSido)}
                                value={selectedSido} 
                                onChange={(e) => { setSelectedSido(e.target.value); setSelectedGugun(''); }}
                            >
                                <option value="">{t(translations.findHospital.regionSido)}</option>
                                {sidoList.map(sido => (
                                    <option key={sido} value={sido}>{regionLabel(sido, sidoI18n)}</option>
                                ))}
                            </select>
                            <select 
                                aria-label={t(translations.findHospital.regionGugun)}
                                value={selectedGugun} 
                                onChange={(e) => setSelectedGugun(e.target.value)} 
                                disabled={!selectedSido}
                            >
                                <option value="">{t(translations.findHospital.regionGugun)}</option>
                                {gugunList.map(gugun => (
                                    <option key={gugun} value={gugun}>{regionLabel(gugun, gugunI18n)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </header>

                <div className="main-content">
                    <div className={`list-panel glass reveal ${isVisible ? 'active' : ''}`} style={{ transitionDelay: '0.2s' }}>
                        <div className="result-info">
                            <strong>{filteredHospitals.length}</strong> {t(translations.findHospital.resultFound)}
                        </div>
                        {t(translations.findHospital.koreanNameNote) && (
                            <div className="korean-name-note">{t(translations.findHospital.koreanNameNote)}</div>
                        )}
                        <div className="list-container" data-lenis-prevent="true">
                            {filteredHospitals.map((hospital) => (
                                <div
                                    key={hospital.id}
                                    className={`hospital-card ${selectedHospitalId === hospital.id ? 'active' : ''}`}
                                    onClick={() => handleHospitalClick(hospital.id)}
                                >
                                    <div className="card-header">
                                        <h3 className="h-name">
                                            {localized(hospital.id, 'name', hospital.name).main}
                                            {localized(hospital.id, 'name', hospital.name).sub && (
                                                <span className="h-ko">{localized(hospital.id, 'name', hospital.name).sub}</span>
                                            )}
                                        </h3>
                                        <span className="arrow">▼</span>
                                    </div>
                                    <div className="card-details">
                                        <div className="card-details-inner">
                                            <p className="h-address">
                                                {localized(hospital.id, 'address', hospital.address).main}
                                                {localized(hospital.id, 'address', hospital.address).sub && (
                                                    <span className="h-ko">{localized(hospital.id, 'address', hospital.address).sub}</span>
                                                )}
                                            </p>
                                            <div className="h-info">
                                                {hospital.phone && (
                                                    <div className="info-item">
                                                        <span className="icon">📞</span> {hospital.phone}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="card-actions">
                                                {hospital.website && (
                                                    <a 
                                                        href={hospital.website} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="action-btn website-btn" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            logHospitalAction('click_website', hospital.name);
                                                        }}
                                                    >
                                                        {t(translations.findHospital.website)}
                                                    </a>
                                                )}
                                                {hospital.phone && (
                                                    <a 
                                                        href={`tel:${hospital.phone}`} 
                                                        className="action-btn call-btn" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            logHospitalAction('click_call', hospital.name);
                                                        }}
                                                    >
                                                        {t(translations.findHospital.call)}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`map-panel glass reveal ${isVisible ? 'active' : ''}`} style={{ transitionDelay: '0.4s' }}>
                        <div ref={mapRef} id="map-container"></div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .section-find-hospital {
                    background: #fff;
                    padding: 120px 20px;
                    border-top: 1px solid #eee;
                }
                .container {
                    max-width: 1400px;
                    margin: 0 auto;
                }
                .header-content {
                    text-align: center;
                    margin-bottom: 60px;
                }
                .title {
                    font-size: 2.8rem;
                    font-weight: 800;
                    color: #0A1E4B;
                    margin-bottom: 20px;
                    letter-spacing: -0.02em;
                }
                .subtitle {
                    font-size: 1.2rem;
                    color: #666;
                    margin-bottom: 40px;
                }
                .search-container {
                    max-width: 900px;
                    margin: 0 auto;
                    padding: 30px;
                    border-radius: 30px;
                    display: grid;
                    grid-template-columns: 1fr auto;
                    gap: 15px;
                    background: rgba(248, 249, 250, 0.8);
                }
                .search-container input {
                    padding: 16px 25px;
                    border-radius: 15px;
                    border: 2px solid #eee;
                    font-size: 1.1rem;
                    width: 100%;
                }
                .filter-group {
                    display: flex;
                    gap: 10px;
                }
                .filter-group select {
                    padding: 0 20px;
                    height: 54px;
                    border-radius: 15px;
                    border: 2px solid #eee;
                    font-size: 1rem;
                    background: #fff;
                    min-width: 140px;
                }

                .main-content {
                    display: grid;
                    grid-template-columns: 450px 1fr;
                    gap: 30px;
                    height: 800px;
                }

                .list-panel {
                    background: #fff;
                    border-radius: 30px;
                    border: 1px solid #eee;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.03);
                }

                .result-info {
                    padding: 25px;
                    border-bottom: 1px solid #eee;
                    font-size: 1.1rem;
                    color: #555;
                }
                .result-info strong {
                    color: #0055A5;
                    font-size: 1.4rem;
                }

                .korean-name-note {
                    padding: 12px 25px;
                    background: rgba(0, 85, 165, 0.04);
                    border-bottom: 1px solid #eee;
                    font-size: 0.85rem;
                    line-height: 1.5;
                    color: #667;
                }

                .list-container {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                }

                .hospital-card {
                    background: #fff;
                    border-radius: 15px;
                    border: 1px solid #eee;
                    margin-bottom: 12px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .hospital-card.active {
                    border-color: #0055A5;
                    background: #f8fbff;
                }
                .card-header {
                    padding: 20px 25px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .h-name {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: #0A1E4B;
                    margin: 0;
                }
                .arrow {
                    font-size: 0.8rem;
                    color: #ccc;
                    transition: transform 0.3s ease, color 0.3s ease;
                }
                .hospital-card.active .arrow {
                    color: #0055A5;
                    transform: rotate(180deg);
                }

                .card-details {
                    max-height: 0;
                    opacity: 0;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .hospital-card.active .card-details {
                    max-height: 300px;
                    opacity: 1;
                }
                .card-details-inner {
                    padding: 0 25px 25px;
                    border-top: 1px dashed #eee;
                    margin-top: 5px;
                    padding-top: 20px;
                }
                .h-ko {
                    display: block;
                    margin-top: 3px;
                    font-weight: 400;
                    font-size: 0.82em;
                    color: #8b93a6;
                    line-height: 1.45;
                }
                .h-address {
                    color: #666;
                    font-size: 0.95rem;
                    line-height: 1.6;
                    margin-bottom: 15px;
                }
                .h-info {
                    margin-bottom: 20px;
                }
                .info-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #555;
                    font-size: 0.95rem;
                }
                .card-actions {
                    display: flex;
                    gap: 10px;
                }
                .action-btn {
                    flex: 1;
                    padding: 12px;
                    border-radius: 10px;
                    text-align: center;
                    font-weight: 700;
                    font-size: 0.9rem;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }
                .website-btn {
                    background: #f0f2f5;
                    color: #0A1E4B;
                }
                .website-btn:hover {
                    background: #e4e7eb;
                }
                .call-btn {
                    background: #0055A5;
                    color: #fff;
                }
                .call-btn:hover {
                    background: #004488;
                }

                .map-panel {
                    background: #f8f9fa;
                    border-radius: 30px;
                    overflow: hidden;
                    border: 1px solid #eee;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.03);
                }
                #map-container {
                    width: 100%;
                    height: 100%;
                }

                @media (max-width: 1200px) {
                    .main-content {
                        grid-template-columns: 1fr;
                        height: auto;
                    }
                    .list-panel {
                        height: 450px;
                    }
                    .map-panel {
                        height: 400px;
                    }
                }

                @media (max-width: 768px) {
                    .section-find-hospital {
                        padding: 80px 15px;
                    }
                    .title {
                        font-size: 2rem;
                    }
                    .search-container {
                        grid-template-columns: 1fr;
                        padding: 20px;
                    }
                    .filter-group {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                    }
                    
                    /* Mobile changes for list to show 4 items + lower height */
                    .list-panel {
                        height: 350px; /* Reduces height to allow map to be seen */
                    }
                    .h-name {
                        font-size: 1.05rem; /* Smaller font for hospital names */
                    }
                    .card-header {
                        padding: 15px 20px; /* Reduced vertical padding */
                    }
                    .hospital-card {
                        margin-bottom: 8px; /* Tighter list margin */
                    }
                    .h-address {
                        font-size: 0.85rem;
                    }
                    .info-item {
                        font-size: 0.85rem;
                    }
                    .card-details-inner {
                        padding: 0 20px 20px;
                    }
                }
            `}</style>
        </section>
    );
}
